import React from "react";
import Helper from "../functions/Helper.js";
import { Path, Polygon, Circle } from "@react-pdf/renderer";

function getAngleOfRotation(vector) {
  let [x, y] = vector;
  if (x === 0 && y === 0) {
    return 0; // Edge case for zero vector, return 0 degrees
  }
  const angleRad = Math.atan2(y, x);

  return angleRad - Math.PI / 2;
}

const getEndVector = (shape, s, m, e, h1, h2) => {
  switch (shape) {
    case 0: {
      return [e[0] - s[0], e[1] - s[1]];
    }
    case 1: {
      let b = Helper.calculateBezierPoint(s, m, e);
      return [2 * (e[0] - b[0]), 2 * (e[1] - b[1])];
    }
    case 2: {
      return [e[0] - m[0], e[1] - m[1]];
    }
    case 3: {
      let b = Helper.calculateBezierPoint(m, h2, e);
      return [2 * (e[0] - b[0]), 2 * (e[1] - b[1])];
    }
    default: {
    }
  }
};

const getStrokeDashArray = (props) => {
  switch (props.subType) {
    case "pass":
      return props.scalingFactor * 1.2 + "," + 0.4 * props.scalingFactor;
    case "shot":
      return props.scalingFactor * 1.2 + "," + 0.4 * props.scalingFactor;
    default:
      return "";
  }
};

const newTipForMarker = (e, endVector, dist) => {
  let n = Helper.normalizeVector(endVector);
  return Helper.roundCoords([e[0] + dist * n[0], e[1] + dist * n[1]]);
};

const getPolygonPoints = (e, width, height, yOffset) => {
  const tip = [e[0], e[1] + yOffset];
  const leftBase = [e[0] - width / 2, e[1] - height + yOffset];
  const rightBase = [e[0] + width / 2, e[1] - height + yOffset];
  const middleBase = [e[0], e[1] - height * (15 / 16) + yOffset];
  return `${tip[0]} ${tip[1]} ${leftBase[0]} ${leftBase[1]} ${middleBase[0]} ${middleBase[1]} ${rightBase[0]} ${rightBase[1]} `;
};

const getScreenPolygonPoints = (e, width, height, yOffset) => {
  const leftBottom = [e[0] - width / 2, e[1] - yOffset];
  const leftTop = [e[0] - width / 2, e[1] + height - yOffset];
  const rightBottom = [e[0] + width / 2, e[1] - yOffset];
  const rightTop = [e[0] + width / 2, e[1] + height - yOffset];
  return `${leftBottom[0]} ${leftBottom[1]} ${leftTop[0]} ${leftTop[1]} ${rightTop[0]} ${rightTop[1]} ${rightBottom[0]} ${rightBottom[1]}`;
};

export default function Arrow(props) {
  let { playerCircleRadius } = props.objectSizes;
  let arrowWidth = 2 * props.objectSizeFactor;
  let arrowHeight = 1.75 * props.objectSizeFactor;
  let playerRadius = playerCircleRadius * props.objectSizeFactor;

  let linkedEnds = props.links.map((x) => x.point);

  let curvedPath =
    props.subType === "dribble"
      ? Helper.createWavyPath(
          Helper.calculatePointsOnCurve(
            linkedEnds,
            props.shape,
            props.s,
            props.m,
            props.e,
            props.h1,
            props.h2,
            playerRadius
          )
        )
      : Helper.createPathV2(props, props.links, playerRadius);

  let endVector = getEndVector(
    props.shape,
    props.s,
    props.m,
    props.e,
    props.h1,
    props.h2
  );
  let angle = getAngleOfRotation(endVector);

  let adjustedEndPoints = Helper.adjustPointsV2(
    props,
    props.links,
    playerRadius
  );

  let newTip = newTipForMarker(adjustedEndPoints.e, endVector, -0.3);

  if (isNaN(newTip[0])) {
    return null;
  }

  return (
    <>
      <Path
        id={props.id + (!props.editable ? "phase_" + props.phaseId : "") + "_p"}
        d={curvedPath}
        style={{
          strokeDasharray: getStrokeDashArray(props),
        }}
        strokeWidth={0.25 * props.scalingFactor * props.objectSizeFactor}
        fill="none"
        stroke={props.color}
      />

      {
        // red dot at adjustedEndPoints.e
        /*
        <Circle
          cx={adjustedEndPoints.e[0]}
          cy={adjustedEndPoints.e[1]}
          r={0.2 * props.scalingFactor * props.objectSizeFactor}
          fill="red"
        /> */
      }
      {(props.subType === "cut" ||
        props.subType === "dribble" ||
        props.subType === "pass") && (
        <Polygon
          style={{
            transform: "rotate(" + angle + "rad)",
            transformOrigin: newTip[0] + " " + newTip[1],
          }}
          fill={props.color}
          fillOpacity={1}
          shapeRendering="geometricPrecision"
          points={getPolygonPoints(
            newTip,
            arrowWidth * props.scalingFactor,
            arrowHeight * props.scalingFactor,
            (arrowWidth * props.scalingFactor) / 3
          )}
        />
      )}

      {props.subType === "screen" && (
        <Polygon
          style={{
            transform: "rotate(" + angle + "rad)",
            transformOrigin:
              adjustedEndPoints.e[0] + " " + adjustedEndPoints.e[1],
          }}
          fill={props.color}
          shapeRendering="geometricPrecision"
          points={getScreenPolygonPoints(
            adjustedEndPoints.e,
            2.75 * props.scalingFactor * props.objectSizeFactor,
            0.25 * props.scalingFactor * props.objectSizeFactor,
            0 * props.scalingFactor * props.objectSizeFactor
          )}
        />
      )}

      {props.subType === "shot" && (
        <>
          <Circle
            cx={props.e[0]}
            cy={props.e[1]}
            r={props.scalingFactor * props.objectSizeFactor}
            style={{
              transform: "rotate(" + angle + "rad)",
              transformOrigin: props.e[0] + " " + props.e[1],
            }}
            fill={"none"}
            strokeWidth={0.25 * props.scalingFactor * props.objectSizeFactor}
            stroke={props.color}
            shapeRendering="geometricPrecision"
          />
          <Path
            style={{
              transform: "rotate(" + angle + "rad)",
              transformOrigin: props.e[0] + " " + props.e[1],
            }}
            strokeWidth={0.25 * props.scalingFactor * props.objectSizeFactor}
            stroke={props.color}
            d={`M ${
              props.e[0] -
              (10 / 7) * props.scalingFactor * props.objectSizeFactor
            } ${props.e[1]} L ${
              props.e[0] -
              (4 / 7) * props.scalingFactor * props.objectSizeFactor
            } ${props.e[1]}`}
          />
          <Path
            style={{
              transform: "rotate(" + angle + "rad)",
              transformOrigin: props.e[0] + " " + props.e[1],
            }}
            strokeWidth={0.25 * props.scalingFactor * props.objectSizeFactor}
            stroke={props.color}
            d={`M ${
              props.e[0] +
              (10 / 7) * props.scalingFactor * props.objectSizeFactor
            } ${props.e[1]} L ${
              props.e[0] +
              (4 / 7) * props.scalingFactor * props.objectSizeFactor
            } ${props.e[1]}`}
          />

          <Path
            style={{
              transform: "rotate(" + angle + "rad)",
              transformOrigin: props.e[0] + " " + props.e[1],
            }}
            strokeWidth={0.25 * props.scalingFactor * props.objectSizeFactor}
            stroke={props.color}
            d={`M ${props.e[0]} ${
              props.e[1] +
              (10 / 7) * props.scalingFactor * props.objectSizeFactor
            } L ${props.e[0]} ${
              props.e[1] +
              (4 / 7) * props.scalingFactor * props.objectSizeFactor
            }`}
          />

          <Path
            style={{
              transform: "rotate(" + angle + "rad)",
              transformOrigin: props.e[0] + " " + props.e[1],
            }}
            strokeWidth={0.25 * props.scalingFactor * props.objectSizeFactor}
            stroke={props.color}
            d={`M ${props.e[0]} ${
              props.e[1] -
              (10 / 7) * props.scalingFactor * props.objectSizeFactor
            } L ${props.e[0]} ${
              props.e[1] -
              (4 / 7) * props.scalingFactor * props.objectSizeFactor
            }`}
          />

          {/*
        <path
          strokeDasharray={"1000"}
          strokeWidth={scaledStrokeWidth}
          stroke={color}
          d={`M 10 0 L 3 0 `}
        />

        <path
          strokeDasharray={"1000"}
          strokeWidth={scaledStrokeWidth}
          stroke={color}
          d={`M  0 10  L  0 3 `}
        />
        <path
          strokeDasharray={"1000"}
          strokeWidth={scaledStrokeWidth}
          stroke={color}
          d={`M  0 -10  L  0 -3 `}
        />*/}
        </>
      )}
    </>
  );
}
